import { create } from 'zustand';

import {
  LOG_DEFAULT_SORT,
  LOG_DEFAULT_SORT_DIR,
  type LogCategory,
  type LogSortDir,
  type LogSortKey,
  type LogSource,
  type LogStatus,
  type LogUserType,
} from '../types/log.types';

// 'all' is the FE-side placeholder for "no filter"; converted to undefined
// before the API call.
export type LogSourceFilter = LogSource | 'all';
export type LogUserTypeFilter = LogUserType | 'all';
export type LogCategoryFilter = LogCategory | 'all';
export type LogStatusFilter = LogStatus | 'all';

export interface LogInquiryListFilters {
  // Partial match on actorName / action / target. Real-time apply.
  search: string;
  source: LogSourceFilter;
  userType: LogUserTypeFilter;
  category: LogCategoryFilter;
  status: LogStatusFilter;
  // YYYY-MM-DD (KST). Applied on [조회] click — see LogInquiryFilters.
  dateFrom: string | null;
  dateTo: string | null;
}

interface LogInquiryListState {
  filters: LogInquiryListFilters;
  // Zero-based index of the page currently on screen.
  page: number;
  sort: LogSortKey;
  dir: LogSortDir;
  setFilters: (partial: Partial<LogInquiryListFilters>) => void;
  setPage: (page: number) => void;
  // Clicking a column header: the same column flips direction, a new column
  // starts descending.
  toggleSort: (key: LogSortKey) => void;
  reset: () => void;
}

const INITIAL_FILTERS: LogInquiryListFilters = {
  search: '',
  source: 'all',
  userType: 'all',
  category: 'all',
  status: 'all',
  dateFrom: null,
  dateTo: null,
};

// Paging and sorting both live here because both are server-side now: the page
// number and the sort column travel to the backend on every list call.
//
// The one rule that keeps the screen coherent: changing ANY filter, the search
// text or the sort resets the page back to the first one. Without it the user
// lands on "page 7 of 2 pages" and sees an empty table for a search that
// actually has results.
export const useLogInquiryListStore = create<LogInquiryListState>((set) => ({
  filters: INITIAL_FILTERS,
  page: 0,
  sort: LOG_DEFAULT_SORT,
  dir: LOG_DEFAULT_SORT_DIR,
  setFilters: (partial) =>
    set((s) => ({ filters: { ...s.filters, ...partial }, page: 0 })),
  setPage: (page) => set({ page: Math.max(0, page) }),
  toggleSort: (key) =>
    set((s) => ({
      sort: key,
      dir: s.sort === key && s.dir === 'desc' ? 'asc' : 'desc',
      page: 0,
    })),
  reset: () =>
    set({
      filters: INITIAL_FILTERS,
      page: 0,
      sort: LOG_DEFAULT_SORT,
      dir: LOG_DEFAULT_SORT_DIR,
    }),
}));
